import { debounce } from 'lodash';
import { forwardRef, PropsWithChildren, Ref, useCallback, useEffect, useState } from 'react';

type OrNull<T> = T | null;

export interface InputFieldProps {
  value: string | number;
  name?: string;
  placeholder?: string;
  autoFocus?: boolean;
  autoComplete?: string;
  type?: string;
  valid?: boolean;
  onChange?: (e) => void;
  onBlur?: (e) => void;
  onFocus?: (e) => void;
  readOnly?: boolean;
  className?: string;
  inputClassName?: string;
  min?: number;
  max?: number;
  required?: boolean;
}

const TextInput = forwardRef(
  (
    {
      value,
      valid = true,
      name,
      placeholder,
      readOnly,
      type = 'text',
      onChange,
      onBlur,
      onFocus,
      className,
      autoFocus,
      autoComplete,
      inputClassName,
      min,
      max,
      required,
      children,
    }: PropsWithChildren<InputFieldProps>,
    ref: Ref<OrNull<HTMLInputElement>>,
  ) => {
    const [text, setText] = useState(value);

    const onChangeText = e => {
      setText(e.target.value);
      debouncedUpdate(e.target.value);
    };

    const debouncedUpdate = useCallback(
      debounce(val => onChange(val), 1000),
      [],
    );

    useEffect(() => setText(value), [value]);

    return (
      <div
        className={
          'w-full dark:text-white dark:bg-howio-700 dark:border-howio-600 border-gray-200 bg-gray-50  appearance-none rounded-md border placeholder-gray-400 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500'
        }
      >
        <input
          min={min}
          max={max}
          ref={ref}
          readOnly={readOnly}
          type={type}
          value={text}
          name={name}
          required={required}
          autoFocus={autoFocus}
          autoComplete={autoComplete}
          placeholder={placeholder}
          onFocus={e => (onFocus ? onFocus(e.target.value) : null)}
          onBlur={e => (onBlur ? onBlur(e.target.value) : null)}
          onChange={onChangeText}
          className={`w-full text-inherit border-none bg-transparent px-3 py-2 focus:border-none focus:ring-none focus:outline-none outline-none ring-none ${
            valid ? '' : 'border-red-600'
          } rounded-md ${inputClassName}`}
        />
        {children}
      </div>
    );
  },
);

export default TextInput;
